import { ContextMenu } from "radix-ui";
import { useEffect, useRef, type ReactNode } from "react";
import { HoverActions, type MoreActions } from "./HoverActions.tsx";
import { MenuEntries } from "./MenuEntries.tsx";
import { color, cx, radius as radiusVar, size as sizeVar, space, type ColorToken, type RadiusToken, type SizeToken, type SpaceToken } from "./tokens.ts";
import type { Align, Justify, MenuEntry, MenuItem } from "./types.ts";

export type StackProps = {
  direction?: "vertical" | "horizontal";
  gap?: SpaceToken;
  padding?: SpaceToken;
  paddingX?: SpaceToken;
  paddingY?: SpaceToken;
  align?: Align;
  justify?: Justify;
  grow?: boolean;
  wrap?: boolean;
  /** Fixed width (does not grow or shrink), e.g. the label column of the properties panel. */
  width?: SizeToken;
  /** Fixed height (e.g. an inline database block); `grow` children fill it. */
  height?: SizeToken;
  background?: ColorToken;
  /** Web: `background` eases into its next colour (a new row's accent fading back to the row's own). Native: the same, through `.animation` / `animateColorAsState`. */
  fadeBackground?: boolean;
  radius?: RadiusToken;
  border?: ColorToken;
  /** Web: descendants with `revealOnHover` show while this stack is hovered or focused (rows with hover controls). */
  hoverGroup?: boolean;
  /** Web: tinted while hovered or keyboard-focused, or while its hover toolbar's menu is open (message rows, Slack). Native: none. */
  hoverHighlight?: boolean;
  /** Web: hidden until the nearest `hoverGroup` ancestor is hovered/focused (always visible on touch). Native: always visible. */
  revealOnHover?: boolean;
  /**
   * Web: hidden (its space kept) while the nearest `hoverGroup` ancestor is hovered or focused: the trailing text that
   * an `overlay` of hover controls replaces (a mail row's date). Touch and native: always visible.
   */
  hideOnHover?: boolean;
  /**
   * Drawn over the parent's end edge (`end`: full height, children placed by `align`) or top-end corner instead of
   * taking room in its layout. With `revealOnHover`: row controls shown in place of the row's trailing text (Gmail's
   * row actions over the date); such an overlay is not shown on touch screens, where the row's context menu has the
   * same actions. iOS: the parent's `.overlay(alignment: .trailing / .topTrailing)`; Android: `Box` +
   * `Modifier.align(Alignment.CenterEnd / TopEnd)`.
   */
  overlay?: "end" | "top-end";
  /**
   * Keeps its content's natural width in a row: never shrinks, so its text never wraps (a row's date or time); `grow`
   * siblings give way. iOS `.fixedSize()`; Android: no `weight`, measured before weighted siblings.
   */
  fixedSize?: boolean;
  /** Web: floating toolbar shown on hover/focus (top-end). Native: merged into the long-press context menu. */
  hoverActions?: MenuItem[];
  /**
   * More actions behind a trailing "…" in the hover toolbar (`label` names that button), e.g. "Delete message". The
   * row's context menu (web: right-click) lists the hover actions that have an icon, then these. Native: appended to
   * the long-press context menu.
   */
  moreActions?: MoreActions;
  /**
   * "listbox": the stack holds the options of a keyboard-driven list (autocomplete, slash menu) whose rows are
   * `Pressable highlighted`. iOS `.accessibilityElement(children: .contain)`, Android `semantics { selectableGroup() }`.
   */
  accessibilityRole?: "listbox";
  /**
   * Scrolls the nearest scroll container just enough to show the whole stack when it appears and whenever it grows
   * (an in-place editor in a chat list). iOS: `ScrollViewReader.scrollTo(id)`; Android: `BringIntoViewRequester`.
   */
  keepInView?: boolean;
  /** Name of a `listbox` stack ("People", "Commands"). */
  accessibilityLabel?: string;
  testID?: string;
  children?: ReactNode;
};

const alignMap = { start: "flex-start", center: "center", end: "flex-end", stretch: "stretch" } as const;
const justifyMap = { start: "flex-start", center: "center", end: "flex-end", between: "space-between" } as const;

export function Stack(p: StackProps) {
  const horizontal = p.direction === "horizontal";
  const px = p.paddingX ?? p.padding;
  const py = p.paddingY ?? p.padding;
  const more = p.moreActions !== undefined && p.moreActions.items.length > 0 ? p.moreActions : undefined;
  const hasActions = (p.hoverActions !== undefined && p.hoverActions.length > 0) || more !== undefined;
  const ref = useRef<HTMLDivElement>(null);
  const keepInView = p.keepInView === true;
  useEffect(() => {
    const node = ref.current;
    if (!keepInView || node === null) return undefined;
    const reveal = () => node.scrollIntoView({ block: "nearest" });
    const id = requestAnimationFrame(reveal);
    const ro = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(reveal);
    ro?.observe(node);
    return () => {
      cancelAnimationFrame(id);
      ro?.disconnect();
    };
  }, [keepInView]);
  const el = (
    <div
      ref={ref}
      className={cx(
        "b-stack",
        horizontal && "b-stack-h",
        p.grow && "b-grow",
        p.wrap && "b-wrap",
        (hasActions || p.hoverGroup === true) && "b-hover-group",
        p.revealOnHover === true && "b-reveal",
        p.hideOnHover === true && "b-hide-on-hover",
        p.overlay !== undefined && `b-overlay b-overlay-${p.overlay}`,
        p.fixedSize === true && "b-fixed-size",
        p.hoverHighlight === true && "b-hover-highlight",
        p.fadeBackground === true && "b-bg-fade",
      )}
      data-testid={p.testID}
      role={p.accessibilityRole}
      aria-label={p.accessibilityRole === undefined ? undefined : p.accessibilityLabel}
      style={{
        gap: p.gap === undefined ? undefined : space(p.gap),
        paddingLeft: px === undefined ? undefined : space(px),
        paddingRight: px === undefined ? undefined : space(px),
        paddingTop: py === undefined ? undefined : space(py),
        paddingBottom: py === undefined ? undefined : space(py),
        // Contract default: stretch in both directions (CSS flex default); rows that centre say align="center".
        alignItems: p.align === undefined ? "stretch" : alignMap[p.align],
        justifyContent: p.justify === undefined ? undefined : justifyMap[p.justify],
        background: p.background === undefined ? undefined : color(p.background),
        borderRadius: p.radius === undefined ? undefined : radiusVar(p.radius),
        border: p.border === undefined ? undefined : `1px solid ${color(p.border)}`,
        width: p.width === undefined ? undefined : sizeVar(p.width),
        height: p.height === undefined ? undefined : sizeVar(p.height),
        flex: p.width === undefined && p.height === undefined ? undefined : "none",
      }}
    >
      {p.children}
      {hasActions ? <HoverActions items={p.hoverActions ?? []} more={more} /> : null}
    </div>
  );
  if (more === undefined) return el;
  const menu: MenuEntry[] = (p.hoverActions ?? []).filter((it) => it.icon !== undefined);
  if (menu.length > 0) menu.push({ id: "more-separator", separator: true });
  menu.push(...more.items);
  return (
    <ContextMenu.Root modal={false}>
      <ContextMenu.Trigger asChild>{el}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="b-menu" data-testid={p.testID === undefined ? undefined : `${p.testID}-context-menu`}>
          <MenuEntries kind="context" entries={menu} />
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
